import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './TipDrawer.css';

const PRESET_AMOUNTS = [0.5, 1, 1.5, 2, 2.5];

function TipDrawer({
  onClose,
  onSend,
  disableHistoryTrap = false,
  defaultAmount = 0.5,
}) {
  const [amount, setAmount] = useState(String(defaultAmount));
  const [selectedPreset, setSelectedPreset] = useState(0);
  const [message, setMessage] = useState('');

  const pushedRef = useRef(false);
  const closingFromButtonRef = useRef(false);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    setAmount(String(defaultAmount));
    const presetIndex = PRESET_AMOUNTS.findIndex(
      (v) => Number(v) === Number(defaultAmount)
    );
    setSelectedPreset(presetIndex >= 0 ? presetIndex : null);
  }, [defaultAmount]);

  const safeClose = useCallback(({ manual }) => {
    if (manual) closingFromButtonRef.current = true;

    try {
      onCloseRef.current?.();
    } catch {}

    if (
      manual &&
      !disableHistoryTrap &&
      pushedRef.current &&
      window.history.state?.bfDrawer === 'tip'
    ) {
      try {
        window.history.back();
      } catch {}
      pushedRef.current = false;
    }
  }, [disableHistoryTrap]);

  const handleBackdropClick = useCallback((e) => {
    if (e.target !== e.currentTarget) return;
    safeClose({ manual: true });
  }, [safeClose]);

  const stopDrawerPropagation = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const handlePresetClick = useCallback((value, index) => {
    setAmount(String(value));
    setSelectedPreset(index);
  }, []);

  useEffect(() => {
    const body = document.body;
    const prevOverflow = body.style.overflow;

    body.style.overflow = 'hidden';
    body.classList.add('bf-tip-drawer-open');

    if (disableHistoryTrap) {
      return () => {
        body.style.overflow = prevOverflow;
        body.classList.remove('bf-tip-drawer-open');
      };
    }

    try {
      window.history.pushState({ bfDrawer: 'tip' }, '');
      pushedRef.current = true;
    } catch {}

    const onPopState = () => {
      if (closingFromButtonRef.current) {
        closingFromButtonRef.current = false;
        return;
      }

      safeClose({ manual: false });
    };

    window.addEventListener('popstate', onPopState);

    return () => {
      body.style.overflow = prevOverflow;
      body.classList.remove('bf-tip-drawer-open');

      window.removeEventListener('popstate', onPopState);

      if (
        pushedRef.current &&
        window.history.state?.bfDrawer === 'tip'
      ) {
        try {
          window.history.back();
        } catch {}
        pushedRef.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disableHistoryTrap]);

  const handleSend = () => {
    if (!amount || isNaN(amount) || Number(amount) <= 0) {
      alert('Enter a valid Pi amount');
      return;
    }

    onSend({
      amount: parseFloat(amount),
      message,
    });

    safeClose({ manual: true });
  };


return createPortal(
  <div className="tip-drawer-backdrop" onClick={handleBackdropClick}>


      <div
        className="tip-drawer-content"
        onClick={stopDrawerPropagation}
        onMouseDown={stopDrawerPropagation}
        onTouchStart={stopDrawerPropagation}
      >
        <div className="tip-header">
          <span>Send Tip</span>
          <button
            className="tip-close"
            onClick={() => safeClose({ manual: true })}
          >
            ✕
          </button>
        </div>

        <input
          type="number"
          className="tip-input"
          placeholder="Amount (Pi)"
          value={amount}
          onChange={(e) => {
            setAmount(e.target.value);
            setSelectedPreset(null);
          }}
        />

        <div className="tip-presets">
          {PRESET_AMOUNTS.map((val, i) => (
            <button
              key={val}
              type="button"
              className={`tip-preset-btn ${selectedPreset === i ? 'active' : ''}`}
              onClick={() => handlePresetClick(val, i)}
            >
              {val} Pi
            </button>
          ))}
        </div>

        <textarea
          className="tip-textarea"
          placeholder="Optional message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />

        <button className="tip-send-btn" onClick={handleSend}>
          Send Tip
        </button>
      </div>

  </div>,
  document.body
);
}

export default TipDrawer;
